/**
 * App — Root component and screen state machine
 *
 * Layout principle: main always fills 100dvh (viewport-filling).
 * Footer lies below the fold — only reachable by scroll.
 *
 * @see docs/ims-brand-frontends/08-ui-screens.md#layout-prinzip-viewport-filling
 * @see docs/ims-brand-frontends/04-state-management.md
 */
import { useEffect, useRef, useState } from 'react';
import { useAppStore } from './store/app.store';
import { useConfig } from './hooks/useConfig';
import { useTracking } from './hooks/useTracking';
import { useDesktopGuard } from './hooks/useDesktopGuard';
import { QrScreen } from './components/screens/QrScreen';
import { StartScreen } from './components/screens/StartScreen';
import { EmbedLoader } from './components/screens/EmbedLoader';
import { Viewer } from './components/screens/Viewer';
import { Footer } from './components/layout/Footer';
import { CookieConsent } from './components/overlays/CookieConsent';
import { ErrorOverlay } from './components/overlays/ErrorOverlay';
import { CtaPanel } from './components/ui/CtaPanel';
import { ArModal } from './components/overlays/ArModal';
import { ArQrModal } from './components/overlays/ArQrModal';
import { detectLayout } from './utils/detectLayout';
import { useKioskReset } from './hooks/useKioskReset';

export function App() {
  const screen = useAppStore((s) => s.screen);
  const setScreen = useAppStore((s) => s.setScreen);
  const sceneReady = useAppStore((s) => s.sceneReady);
  const consentGiven = useAppStore((s) => s.consentGiven);
  const error = useAppStore((s) => s.error);
  const embedMode = useAppStore((s) => s.embedMode);
  const arAutoMode = useAppStore((s) => s.arAutoMode);
  const kioskMode = useAppStore((s) => s.kioskMode);
  const transparentBg = useAppStore((s) => s.transparentBg);
  const [arModalOpen, setArModalOpen] = useState(false);
  const isDesktop = detectLayout() === 'desktop';

  // In embed and kiosk modes, hide chrome (cookie banner, footer, CTAs).
  const chromeHidden = embedMode || kioskMode;

  // ── Auto-AR: open AR modal automatically when ?ar=auto is present on mobile ──
  const autoArFired = useRef(false);
  useEffect(() => {
    if (!sceneReady || isDesktop || autoArFired.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('ar') !== 'auto') return;
    autoArFired.current = true;
    // Clean the param from URL so a reload won't re-trigger
    params.delete('ar');
    const clean = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
    window.history.replaceState(null, '', clean);
    // Small delay so the viewer is fully rendered before AR overlay opens
    const t = setTimeout(() => setArModalOpen(true), 600);
    return () => clearTimeout(t);
  }, [sceneReady, isDesktop]);

  // Config is loaded once at app start from URL params (?brand=&model=&market=)
  const { config } = useConfig();

  // ── Tracking: init once config + consent are known; manages session timer ──
  useTracking({ config });

  // ── Desktop guard: redirect to QR screen on desktop when allowDesktop=false ──
  // Skipped in embed mode — embeds always render on any device.
  useDesktopGuard({ config, skip: chromeHidden || arAutoMode });

  // ── Kiosk: auto-reload after inactivity ─────────────────────────────────────────
  useKioskReset();

  // ── Transparent background: override html/body bg when active ─────────────────
  useEffect(() => {
    if (transparentBg) {
      document.documentElement.style.background = 'transparent';
      document.body.style.background = 'transparent';
    }
    return () => {
      document.documentElement.style.background = '';
      document.body.style.background = '';
    };
  }, [transparentBg]);

  // ---------------------------------------------------------------------------
  // Black fade overlay — plays once when the 3D scene is ready
  //
  // Sequence (fully animated — no jumps):
  //   1. mount at opacity:0
  //   2. double-rAF → overlayIn:true  → fade IN  to black (600ms ease-in)
  //   3. 700ms hold while black (switch screen during solid black)
  //   4. overlayIn:false → fade OUT to transparent (700ms ease-out)
  //   5. unmount
  //
  // Critical: transition string NEVER changes between renders — only opacity
  // changes. Changing both transition+opacity in the same render prevents CSS
  // from animating (browser sees no previous transition baseline).
  // ---------------------------------------------------------------------------
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayIn, setOverlayIn] = useState(false);

  useEffect(() => {
    if (!sceneReady) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: coordinated animation sequence (mount overlay, then rAF chain)
    setOverlayVisible(true);
    setOverlayIn(false); // mount at opacity:0

    let t1: ReturnType<typeof setTimeout>;
    let t2: ReturnType<typeof setTimeout>;
    let t3: ReturnType<typeof setTimeout>;
    let raf2: number;

    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setOverlayIn(true); // → fade in (600ms)
        t1 = setTimeout(() => setScreen('viewer'), 900); // switch while solid black
        t2 = setTimeout(() => setOverlayIn(false), 1000); // → fade out (700ms)
        t3 = setTimeout(() => setOverlayVisible(false), 1800); // unmount after fade
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [sceneReady, setScreen]);

  // ── Layout profiles ────────────────────────────────────────────────────────
  //
  //  tl: = @custom-variant (@media min-width:768px AND min-height:500px)
  //      Matches: tablet portrait, tablet landscape, desktop
  //      Excludes: phones portrait (width <768px) + phones landscape (height <500px)
  //
  //  Content screens (start / qr):
  //    Phone portrait/landscape   outer=min-h-svh  main=min-h-svh
  //                               → fills viewport; footer below fold, needs scroll
  //    Tablet portrait/landscape  outer=tl:h-svh   main=tl:flex-1 tl:min-h-0
  //    Desktop                    → viewport locked; footer visible without scroll
  //
  //  3D canvas screens (viewer / loading) — ALL devices:
  //    main=h-svh overflow-hidden → canvas stable, footer always below fold
  //    svh = small viewport height, never changes when browser chrome shows/hides
  //
  const isViewerPhase = screen === 'viewer' || screen === 'loading';
  // QR screen keeps the viewport-locked layout on tablet landscape;
  // start screen should scroll normally so the footer appears below the fold.
  const isFixedContentScreen = screen === 'qr';

  return (
    <div
      className={`min-h-svh flex flex-col font-brand${transparentBg ? '' : ' bg-black'}${isFixedContentScreen ? ' tl:h-svh' : ''}`}
    >
      <main
        className={`relative${
          isViewerPhase
            ? ' h-svh overflow-hidden'
            : isFixedContentScreen
              ? ' min-h-svh tl:flex-1 tl:min-h-0 tl:overflow-hidden'
              : ' min-h-svh'
        }`}
      >
        {/* ── Layer 1: 3D Viewer (absolute, always behind) ────────────────
            Mounted when loading starts so the engine begins loading in the
            background. Invisible until the black fade overlay lifts.        */}
        {(screen === 'loading' || screen === 'viewer') && (
          <div className="absolute inset-0 z-0">
            <Viewer config={config} onArOpen={() => setArModalOpen(true)} />
          </div>
        )}

        {/* ── Layer 2: Start screen or Embed loader (on top during loading) */}
        {!embedMode && !arAutoMode && (screen === 'start' || screen === 'loading') && (
          <div className="absolute inset-0 z-10">
            <StartScreen />
          </div>
        )}
        {(embedMode || arAutoMode) && screen === 'loading' && (
          <div className="absolute inset-0 z-10">
            <EmbedLoader />
          </div>
        )}

        {/* ── Layer 3: QR screen ──────────────────────────────────────── */}
        {screen === 'qr' && (
          <div className="absolute inset-0 z-10">
            <QrScreen config={config} />
          </div>
        )}

        {/* ── Layer 4: Black transition overlay ───────────────────────────
            Appears instantly when sceneReady fires, then fades out slowly
            to reveal the fully-loaded 3D scene beneath.
            IMPORTANT: transition must always be defined so that the opacity
            change from 1→0 is actually animated. Setting transition:'none'
            and opacity:0 in the same render cycle skips the animation.     */}
        {overlayVisible && !transparentBg && (
          <div
            className="absolute inset-0 z-50 bg-black pointer-events-none"
            style={{
              opacity: overlayIn ? 1 : 0,
              transition: 'opacity 600ms ease-in-out',
            }}
          />
        )}

        {/* ── Layer 5: CTA Panel — absolute so it's clipped by main's     */}
        {/* overflow:hidden when user scrolls to footer. z-40 stays below  */}
        {/* the black fade overlay (z-50).                                 */}
        {!chromeHidden && screen === 'viewer' && (
          <CtaPanel config={config} sceneReady={sceneReady} />
        )}

        {/* ── Layer 6: AR Modal — absolute inside <main> so overflow:hidden  */}
        {/* clips it; footer stays reachable by scroll. z-[100] beats       */}
        {/* CtaPanel (z-40) and MenuBar (z-100) stacking contexts.          */}
        {/* ── AR Modals — desktop shows QR code, mobile shows native AR ── */}
        {screen === 'viewer' && isDesktop && (
          <ArQrModal open={arModalOpen} onClose={() => setArModalOpen(false)} config={config} />
        )}
        {screen === 'viewer' && !isDesktop && (
          <ArModal open={arModalOpen} onClose={() => setArModalOpen(false)} config={config} />
        )}
      </main>

      {/* Footer — hidden in embed & kiosk mode */}
      {!chromeHidden && <Footer config={config} />}

      {/* ── Portals: fixed overlays OUTSIDE <main> ──────────────────────────
          position:fixed breaks when any ancestor has overflow:hidden + a CSS
          transform (e.g. animated absolute children inside <main>). Rendering
          these overlays as siblings of <main> guarantees correct viewport
          anchoring regardless of inner transforms.                           */}
      {!chromeHidden && !consentGiven && <CookieConsent texts={config?.cookies} />}
      {error && <ErrorOverlay error={error} ui={config?.ui} />}
    </div>
  );
}
